import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import { Database, Download, Upload, AlertTriangle, CheckCircle, Clock, FileText, Trash2, RefreshCw } from 'lucide-react';

const DATABASE_TABS = [
  { label: 'Vue & Backups', to: '/admin/database', exact: true, icon: <Database className="w-4 h-4" /> },
  { label: 'Import de données', to: '/admin/import-data', exact: true, icon: <Upload className="w-4 h-4" /> },
];

const AdminDatabasePage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/admin/db-logs');
      setLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/db-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleExport = async () => {
    if (!window.confirm('Exporter toute la base de données ?')) return;
    setLoading(true);
    setMessage(null);
    try {
      const response = await api.get('/admin/export-db', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `remobiles_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Export réussi !' });
      fetchLogs();
    } catch (error) {
      const errorMessage = tError(error.response?.data?.detail) || error.message || 'Erreur lors de l\'export';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier' });
      return;
    }
    if (!window.confirm('⚠️ ATTENTION: Cette action va remplacer les données existantes. Continuer ?')) return;
    
    setLoading(true);
    setMessage(null);
    setImportProgress({ status: 'uploading', message: 'Upload du fichier...' });
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post('/admin/import-db', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportProgress({ status: 'success', message: 'Import terminé !' });
      setMessage({ type: 'success', text: `Import réussi ! ${response.data.message || ''}` });
      setImportFile(null);
      fetchLogs();
      fetchStats();
    } catch (error) {
      setImportProgress({ status: 'error', message: tError(error.response?.data?.detail) || 'Erreur' });
      setMessage({ type: 'error', text: tError(error.response?.data?.detail) || 'Erreur lors de l\'import' });
    } finally {
      setLoading(false);
    }
  };

  const handleImportClients = async () => {
    if (!importFile) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier JSON avec les clients' });
      return;
    }
    if (!window.confirm('Importer les clients ? Les comptes seront créés avec le numéro de téléphone comme identifiant.')) return;
    
    setLoading(true);
    setMessage(null);
    setImportProgress({ status: 'uploading', message: 'Import des clients...' });
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post('/admin/import-clients', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportProgress({ status: 'success', message: 'Import terminé !' });
      setMessage({ type: 'success', text: `${response.data.imported || 0} clients importés, ${response.data.accounts_created || 0} comptes créés` });
      setImportFile(null);
      fetchLogs();
      fetchStats();
    } catch (error) {
      setImportProgress({ status: 'error', message: tError(error.response?.data?.detail) || 'Erreur' });
      setMessage({ type: 'error', text: tError(error.response?.data?.detail) || 'Erreur lors de l\'import' });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-400">{t('adminDb.accessDenied')}</h2>
            <p className="text-gray-400 mt-2">{t('adminDb.adminOnly')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in space-y-6" data-testid="admin-database-page">
        <PageTabBar tabs={DATABASE_TABS} activeColor="amber" />
        <div className="flex items-center gap-3">
          <Database className="w-8 h-8 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold">{t('adminDb.title')}</h1>
            <p className="text-gray-400 text-sm">{t('adminDb.subtitle')}</p>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-auto text-gray-400 hover:text-white">✕</button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass p-4 text-center">
              <div className="text-3xl font-bold text-cyan-400">{stats.clients || 0}</div>
              <div className="text-sm text-gray-400">{t('adminDb.clients')}</div>
            </div>
            <div className="glass p-4 text-center">
              <div className="text-3xl font-bold text-green-400">{stats.users || 0}</div>
              <div className="text-sm text-gray-400">{t('adminDb.users')}</div>
            </div>
            <div className="glass p-4 text-center">
              <div className="text-3xl font-bold text-orange-400">{stats.devices || 0}</div>
              <div className="text-sm text-gray-400">{t('adminDb.repairs')}</div>
            </div>
            <div className="glass p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">{stats.stock || 0}</div>
              <div className="text-sm text-gray-400">{t('adminDb.stockArticles')}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Export Section */}
          <div className="glass p-6 space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Download className="w-6 h-6 text-green-400" />
              {t('adminDb.exportTitle')}
            </h2>
            <p className="text-gray-400 text-sm">
              {t('adminDb.exportDesc')}
            </p>
            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="export-db-btn"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              {t('adminDb.exportBtn')}
            </button>
          </div>

          {/* Import Section */}
          <div className="glass p-6 space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Upload className="w-6 h-6 text-orange-400" />
              {t('adminDb.importTitle')}
            </h2>
            <p className="text-gray-400 text-sm">
              {t('adminDb.importDesc')}
            </p>
            <input
              type="file"
              accept=".json"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-cyan-500 file:text-white file:font-semibold"
              data-testid="import-file-input"
            />
            {importFile && (
              <div className="text-sm text-cyan-400">
                {t('adminDb.fileSelected')}: {importFile.name}
              </div>
            )}
            {importProgress && (
              <div className={`text-sm p-2 rounded ${importProgress.status === 'success' ? 'text-green-400' : importProgress.status === 'error' ? 'text-red-400' : 'text-yellow-400'}`}>
                {importProgress.message}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleImportClients}
                disabled={loading || !importFile}
                className="flex-1 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="import-clients-btn"
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {t('adminDb.importClientsBtn')}
              </button>
              <button
                onClick={handleImport}
                disabled={loading || !importFile}
                className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="import-db-btn"
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {t('adminDb.importFullBtn')}
              </button>
            </div>
            <p className="text-xs text-red-400">
              {t('adminDb.importWarning')}
            </p>
          </div>
        </div>

        {/* Logs */}
        <div className="glass p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
            <FileText className="w-6 h-6 text-purple-400" />
            {t('adminDb.logsTitle')}
          </h2>
          {logs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('adminDb.noLogs')}</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {logs.map((log, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 text-sm">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">{new Date(log.timestamp).toLocaleString('fr-FR')}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${log.type === 'export' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {log.type?.toUpperCase()}
                  </span>
                  <span className="text-white flex-1">{log.message}</span>
                  <span className="text-gray-500">{log.user}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDatabasePage;
